import React, { useState } from "react";
import { Mail, Loader2, Newspaper, Copy } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const CATEGORIES = [
    { value: "motorcycle", label: "Motorrad" },
    { value: "bicycle", label: "Fahrrad" },
];

const REGIONS = ["DE", "Bayern", "NRW", "Berlin", "Hamburg", "Baden-Württemberg"];

export default function AdminNewsletterGenerator() {
    const [region, setRegion] = useState("DE");
    const [category, setCategory] = useState("motorcycle");
    const [loading, setLoading] = useState(false);
    const [digest, setDigest] = useState(null);

    const generate = async () => {
        setLoading(true);
        setDigest(null);
        try {
            const res = await api.get("/admin/newsletter/digest", { params: { region, category } });
            setDigest(res.data);
            toast.success(res.data.mocked ? "Digest generiert (Mock-Modus)" : "Digest live von Gemini generiert.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Newsletter-Generierung fehlgeschlagen.");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        if (!digest) return;
        const text = `${digest.headline}\n\n${(digest.highlights || []).map((h) => `• ${h}`).join("\n")}\n\n${digest.cta || ""}`;
        navigator.clipboard.writeText(text).then(() => toast.success("Newsletter kopiert."));
    };

    return (
        <div className="bg-white border border-gray-200 rounded-md p-5" data-testid="newsletter-generator">
            <div className="flex items-center gap-2 mb-3">
                <Newspaper className="w-5 h-5 text-twowheels-yellow-foreground" />
                <h2 className="text-lg font-semibold text-gray-900">KI-Newsletter / Wochen-Digest</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
                Gemini erzeugt aus den Wochenstatistiken einen kompletten E-Mail-Digest (Headline · 3-5 Highlights · CTA).
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div>
                    <label className="text-xs font-semibold text-gray-700 block mb-1">Region</label>
                    <select value={region} onChange={(e) => setRegion(e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded-sm px-2 py-1.5"
                        aria-label="Region"
                        data-testid="newsletter-region-select">
                        {REGIONS.map((r) => <option key={r} value={r}>{r === "DE" ? "Ganz Deutschland" : r}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-xs font-semibold text-gray-700 block mb-1">Kategorie</label>
                    <select value={category} onChange={(e) => setCategory(e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded-sm px-2 py-1.5"
                        aria-label="Kategorie"
                        data-testid="newsletter-category-select">
                        {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                </div>
                <div className="flex items-end">
                    <button onClick={generate} disabled={loading}
                        className="w-full bg-twowheels-yellow text-twowheels-yellow-foreground hover:brightness-95 text-sm font-semibold px-3 py-1.5 rounded-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                        data-testid="newsletter-generate-btn">
                        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generiere…</> : <><Mail className="w-4 h-4" /> Digest generieren</>}
                    </button>
                </div>
            </div>

            {digest && (
                <div className="border border-gray-200 rounded-md p-4 bg-gray-50" data-testid="newsletter-digest-result">
                    <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-base font-bold text-gray-900" data-testid="digest-headline">{digest.headline}</h3>
                        <button onClick={copyToClipboard} className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1" data-testid="digest-copy">
                            <Copy className="w-3 h-3" /> Kopieren
                        </button>
                    </div>
                    <ul className="text-sm text-gray-800 space-y-1.5 mb-3 list-disc list-inside" data-testid="digest-highlights">
                        {(digest.highlights || []).map((h, i) => <li key={i}>{h}</li>)}
                    </ul>
                    {digest.cta && (
                        <div className="text-sm font-semibold text-twowheels-yellow-foreground mt-3" data-testid="digest-cta">→ {digest.cta}</div>
                    )}
                    <div className="mt-3 pt-3 border-t border-gray-200 text-[11px] text-gray-500 flex gap-3 flex-wrap">
                        <span>Modell: <span className="font-mono">{digest.model}</span></span>
                        <span>Neue Inserate: <span className="font-semibold">{digest.stats?.new_listings ?? 0}</span></span>
                        <span>Top-Marken: <span className="font-semibold">{(digest.stats?.top_brands || []).map((b) => b.brand).join(", ") || "—"}</span></span>
                        {digest.mocked && <span className="text-amber-700 font-semibold">MOCKED</span>}
                    </div>
                </div>
            )}
        </div>
    );
}
